import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Dimmer, Dropdown, Form, Icon, Loader, Message, Modal, Pagination, Table } from 'semantic-ui-react';
import store from '../../../store';
import { ADMIN_LIST_PAGINATION_ROW_CLASS } from '../list/list';
import { formatDate, formatDateTime } from '../../../utils/shared.utils';
import { stripReturnNotePrefix } from '../../../constants/product-return';
import { ReturnReceiptData, returnLineAmount, returnReceiptTotals } from './return-receipt';
import { fetchReturnReceipt, printReturnReceipts } from '../../utils/returnReceiptPrint';
import './return.scss';

/** Нэг мөр = нэг буцаалтын баримт (нэг удаагийн буцаалтын нэг нийлүүлэгчийн бараанууд). */
interface ReturnListRow {
    returnId: number;
    createdAt: string;
    createdBy: string;
    providerId?: number | null;
    providerName?: string | null;
    productCount: number;
    productNames?: string | null;
    totalReturned: number;
    totalCost: number;
}

const PAGE_SIZE_OPTIONS = [
    { key: 15, value: 15, text: '15' },
    { key: 25, value: 25, text: '25' },
    { key: 50, value: 50, text: '50' },
    { key: 100, value: 100, text: '100' },
];

function formatCost(value: number | string | null | undefined): string {
    if (value == null) return '–';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₮';
}

export default function ReturnHistoryPage() {
    const history = useHistory();
    const today = new Date();
    const past = new Date();
    past.setDate(past.getDate() - 30);

    const [startDate, setStartDate] = useState(formatDate(past));
    const [endDate, setEndDate] = useState(formatDate(today));
    const [productName, setProductName] = useState('');
    const [rows, setRows] = useState<ReturnListRow[]>([]);
    const [page, setPage] = useState({ page: 1, size: 25, totalPages: 1, totalRows: 0 });
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState<ReturnReceiptData | null>(null);
    const [detailErrorMsg, setDetailErrorMsg] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token || token === 'undefined') {
            store.dispatch({ type: 'redirectUrl', data: window.location.pathname });
            history.push('/');
            return;
        }
        fetchReturns(1, page.size);
    }, []);

    const fetchReturns = (requestedPage: number, requestedSize: number) => {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        setLoading(true);
        setErrorMsg('');
        fetch(process.env.REACT_APP_API_URL + 'mongol/returns', {
            method: 'POST',
            headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
            body: JSON.stringify({
                startDate: startDate + ' 00:00:00',
                endDate: endDate + ' 23:59:59',
                productName: productName.trim() || undefined,
                page: { page: requestedPage, size: requestedSize },
            }),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((data) => {
                setRows(Array.isArray(data?.data) ? data.data : []);
                if (data?.page) setPage(data.page);
            })
            .catch(() => setErrorMsg('Жагсаалт ачаалахад алдаа гарлаа.'))
            .finally(() => setLoading(false));
    };

    const openDetail = (returnId: number) => {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        setDetailOpen(true);
        setDetailLoading(true);
        setDetail(null);
        setDetailErrorMsg('');
        fetchReturnReceipt(process.env.REACT_APP_API_URL || '', token, returnId)
            .then(setDetail)
            .catch(() => setDetailErrorMsg('Дэлгэрэнгүй ачаалахад алдаа гарлаа.'))
            .finally(() => setDetailLoading(false));
    };

    // Popup-ыг click дотор синхрон нээх ёстой тул энэ функцийг шууд onClick-ээс дуудна.
    const printReceipt = (returnId: number, setError: (msg: string) => void) => {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        setError('');
        printReturnReceipts({ apiUrl: process.env.REACT_APP_API_URL || '', token, returnIds: [returnId] }).then((result) => {
            if (!result.ok && result.error === 'network') setError('Баримт ачаалахад алдаа гарлаа.');
        });
    };

    const detailTotals = detail ? returnReceiptTotals(detail.rows) : null;

    return (
        <div className="page-admin admin-return-history full-width">
            <Dimmer active={loading} inverted>
                <Loader size="medium" />
            </Dimmer>
            <section className="admin-return-section">
                <div className="form-group">
                    <Link to="/mongol/return">
                        <Icon name="arrow left" />
                        <span className="underline">Буцаалт хийх</span>
                    </Link>
                </div>
                <h1>Буцаалтын түүх</h1>

                <div className="report-filter return-history-filter">
                    <div data-testid="return-history-start-date">
                        <Form.Input
                            label="Эхний өдөр: "
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div data-testid="return-history-end-date">
                        <Form.Input
                            label="Сүүлийн өдөр: "
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div data-testid="return-history-product-filter">
                        <Form.Input
                            label="Бүтээгдэхүүний нэр: "
                            placeholder="Хайх нэрээ оруулна уу"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                        />
                    </div>
                    <Button color="blue" onClick={() => fetchReturns(1, page.size)} data-testid="return-history-search">
                        <Icon name="search" /> Хайх
                    </Button>
                </div>

                {errorMsg && (
                    <Message negative size="small">
                        <Icon name="warning circle" /> {errorMsg}
                    </Message>
                )}

                <p className="muted">Нийт {page.totalRows} баримт</p>

                <Table striped className="admin-list-table" width={16}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>№</Table.HeaderCell>
                            <Table.HeaderCell>Огноо</Table.HeaderCell>
                            <Table.HeaderCell>Нийлүүлэгч</Table.HeaderCell>
                            <Table.HeaderCell>Бүтээгдэхүүн</Table.HeaderCell>
                            <Table.HeaderCell>Буцаасан тоо</Table.HeaderCell>
                            <Table.HeaderCell>Нийт өртөг</Table.HeaderCell>
                            <Table.HeaderCell>Буцаасан админ</Table.HeaderCell>
                            <Table.HeaderCell width="1">Баримт</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={8} data-label="Мэдээлэл">
                                    Буцаалт олдсонгүй.
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            rows.map((row) => (
                                <Table.Row key={'rh-' + row.returnId} data-testid="return-history-row" data-return-id={row.returnId}>
                                    <Table.Cell data-label="№">{row.returnId}</Table.Cell>
                                    <Table.Cell data-label="Огноо">
                                        {row.createdAt ? formatDateTime(new Date(row.createdAt)) : '–'}
                                    </Table.Cell>
                                    <Table.Cell data-label="Нийлүүлэгч">{row.providerName || '–'}</Table.Cell>
                                    <Table.Cell data-label="Бүтээгдэхүүн">
                                        <div className="return-history-products" title={row.productNames || ''}>
                                            {row.productNames || '–'}
                                        </div>
                                        {row.productCount > 1 && <div className="muted-small">{row.productCount} бараа</div>}
                                    </Table.Cell>
                                    <Table.Cell data-label="Буцаасан тоо">
                                        <strong>{row.totalReturned}</strong>
                                    </Table.Cell>
                                    <Table.Cell data-label="Нийт өртөг">{formatCost(row.totalCost)}</Table.Cell>
                                    <Table.Cell data-label="Буцаасан админ">{row.createdBy || '–'}</Table.Cell>
                                    <Table.Cell data-label="Баримт" className="return-history-actions">
                                        <Button
                                            type="button"
                                            size="tiny"
                                            basic
                                            onClick={() => openDetail(row.returnId)}
                                            data-testid="return-history-detail-open"
                                        >
                                            <Icon name="folder open outline" /> Үзэх
                                        </Button>
                                        <Button
                                            type="button"
                                            size="tiny"
                                            basic
                                            color="blue"
                                            onClick={() => printReceipt(row.returnId, setErrorMsg)}
                                            data-testid="return-history-print"
                                        >
                                            <Icon name="print" /> Хэвлэх
                                        </Button>
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table>

                <div className={ADMIN_LIST_PAGINATION_ROW_CLASS}>
                    <Pagination
                        activePage={page.page}
                        totalPages={Math.max(1, page.totalPages)}
                        onPageChange={(_, d) => fetchReturns(Number(d.activePage) || 1, page.size)}
                    />
                    <div className="page-size-selector flex-div justify-content-center align-items-center">
                        <div className="flex-div align-items-center" style={{ marginRight: '10px' }}>
                            ХУУДАСНЫ ХЭМЖЭЭ:
                        </div>
                        <Dropdown
                            selection
                            upward
                            options={PAGE_SIZE_OPTIONS}
                            value={page.size}
                            onChange={(_, d) => fetchReturns(1, Number(d.value) || page.size)}
                            compact
                        />
                    </div>
                </div>
            </section>

            <Modal
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                size="large"
                className="return-history-detail-modal"
                mountNode={typeof document !== 'undefined' ? document.getElementById('admin-modal-mount') || undefined : undefined}
                data-testid="return-history-detail-modal"
            >
                <Modal.Header>Буцаалтын баримт{detail ? ' №' + detail.header.returnId : ''}</Modal.Header>
                <Modal.Content>
                    <Dimmer active={detailLoading} inverted>
                        <Loader size="small" />
                    </Dimmer>
                    {detailErrorMsg && (
                        <Message negative size="small">
                            <Icon name="warning circle" /> {detailErrorMsg}
                        </Message>
                    )}
                    {detail && (
                        <div className="return-history-detail-header">
                            <p>
                                <strong>Нийлүүлэгч:</strong> {detail.header.providerName || '–'}
                                {detail.header.providerEmail ? ' (' + detail.header.providerEmail + ')' : ''}
                            </p>
                            <p>
                                <strong>Огноо:</strong>{' '}
                                {detail.header.createdAt ? formatDateTime(new Date(detail.header.createdAt)) : '–'}
                            </p>
                            <p>
                                <strong>Буцаасан админ:</strong> {detail.header.createdBy || '–'}
                            </p>
                        </div>
                    )}

                    <Table compact striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Бүтээгдэхүүн</Table.HeaderCell>
                                <Table.HeaderCell>Өртөг</Table.HeaderCell>
                                <Table.HeaderCell>Буцаасан тоо</Table.HeaderCell>
                                <Table.HeaderCell>Дүн</Table.HeaderCell>
                                <Table.HeaderCell>Тухайн үеийн өртгийн үлдэгдэл</Table.HeaderCell>
                                <Table.HeaderCell>Дуусах огноо</Table.HeaderCell>
                                <Table.HeaderCell>Тайлбар</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {!detail || detail.rows.length === 0 ? (
                                <Table.Row>
                                    <Table.Cell colSpan={7}>Мөр олдсонгүй.</Table.Cell>
                                </Table.Row>
                            ) : (
                                detail.rows.map((r) => (
                                    <Table.Row key={'drow-' + r.id} data-testid="return-history-detail-row">
                                        <Table.Cell data-label="Бүтээгдэхүүн">
                                            {r.productSlug ? (
                                                <a
                                                    className="underline"
                                                    href={'/product/' + r.productSlug}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {r.productName}
                                                </a>
                                            ) : (
                                                r.productName
                                            )}
                                            {r.countAfter != null && (
                                                <div className="muted-small">Үлдэгдэл (буцаалтын дараа): {r.countAfter}</div>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell data-label="Өртөг">{formatCost(r.importCost)}</Table.Cell>
                                        <Table.Cell data-label="Буцаасан тоо">{r.minusCount}</Table.Cell>
                                        <Table.Cell data-label="Дүн">{formatCost(returnLineAmount(r))}</Table.Cell>
                                        <Table.Cell data-label="Тухайн үеийн өртгийн үлдэгдэл">
                                            {r.warehouseImportCount != null ? r.warehouseImportCount : '–'}
                                        </Table.Cell>
                                        <Table.Cell data-label="Дуусах огноо">
                                            {r.importExpireDate ? String(r.importExpireDate).slice(0, 10) : '–'}
                                        </Table.Cell>
                                        <Table.Cell data-label="Тайлбар">{stripReturnNotePrefix(r.note) || '–'}</Table.Cell>
                                    </Table.Row>
                                ))
                            )}
                        </Table.Body>
                        {detailTotals && detail && detail.rows.length > 1 && (
                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <strong>Нийт</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell />
                                    <Table.HeaderCell>
                                        <strong>{detailTotals.count}</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>
                                        <strong>{formatCost(detailTotals.amount)}</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell colSpan={3} />
                                </Table.Row>
                            </Table.Footer>
                        )}
                    </Table>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        color="blue"
                        disabled={!detail}
                        onClick={() => detail && printReceipt(detail.header.returnId, setDetailErrorMsg)}
                        data-testid="return-history-detail-print"
                    >
                        <Icon name="print" /> Баримт хэвлэх
                    </Button>
                    <Button onClick={() => setDetailOpen(false)} data-testid="return-history-detail-close">Хаах</Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
}
